import { getPercentChange, roundFloatNumber } from '.';
import { RecommendationStatus, RecommendationValueState } from '../infrastructure/enums/meta-data-field.enum';
import { ItemPriceState } from '../models/history-log';
import { Recommendation } from '../models/recommendation';

export interface RepricingItem {
  id: string;
  valueState: RecommendationValueState;
  status: RecommendationStatus;
  state: ItemPriceState,
  factor: number;
  // Current Data
  currentInboundPrice: number; // The struck-through price
  currentMin?: number;
  currentMax?: number;

  // Calculated Data
  calculatedInboundPrice: number;

  // Proposed Data
  newInboundPrice: number;
  newMin?: number;
  newMax?: number;
  /** Raw backend suggestion — used by computeNewMinMax at runtime */
  suggestedMin?: number;
  suggestedMax?: number;
  trendPercentage: number; // e.g., 12 or -12

  // Sales
  isSaleActive: boolean;
  saleSavePercent: number;
  currentSalePrice?: number;

  context: any;
  abstractState: any;
  riskRatio?: number;
  customPrice?: number;
  /** Persisted user-set min/max overrides from recommendationOutput */
  customMinPrice?: number;
  customMaxPrice?: number;
  /** True when this item is part of a revert job */
  isRevert?: boolean;
  entitySnapshot?: any;
}

// --- Shared repricing price utilities ---

/**
 * Derives the reactive sale price from a custom inbound (Your Price) value.
 * Preserves the same sale/inbound ratio as the current Amazon data.
 * Falls back to customInboundPrice when the ratio cannot be determined.
 */
export function getDerivedSalePrice(
  customInboundPrice: number,
  currentSalePrice: number | undefined,
  currentInboundPrice: number,
): number {
  if (!currentInboundPrice || !currentSalePrice) return customInboundPrice;
  return roundFloatNumber(customInboundPrice * (currentSalePrice / currentInboundPrice));
}

/**
 * Returns the live trend percentage based on a custom inbound price vs the current inbound price.
 */
export function getRepricingEffectiveTrend(
  customPrice: number,
  currentPrice: number,
  fallbackTrend: number,
  baselinePrice?: number
): number {
  const baseline = currentPrice || baselinePrice;
  if (!baseline) return fallbackTrend;
  return roundFloatNumber(((customPrice - baseline) / baseline) * 100);
}

/**
 * Validates a repricing price against Amazon constraints and item bounds.
 * @param formatCurrency - formatter for error messages, e.g. (v) => '$' + v
 */
export function validateRepricingPrice(
  price: number | null = null,
  bounds: { min?: number; max?: number; listPrice?: number },
  formatCurrency: (v: number) => string
): string | null {
  if (price == null || price < 0) return 'Price cannot be empty or negative.';
  if (!/^\d+(\.\d{1,2})?$/.test(price.toString())) {
    return 'Price must have a maximum of 2 decimal places.';
  }
  if (bounds.min != null && price < bounds.min) {
    return `Price cannot be less than the minimum price (${formatCurrency(bounds.min)}).`;
  }
  if (bounds.max != null && price > bounds.max) {
    return `Price cannot exceed the maximum price (${formatCurrency(bounds.max)}).`;
  }
  if (price < 1.00) {
    return 'Price must be greater than Amazon minimum referral fee ($1.00).';
  }
  if (bounds.listPrice != null) {
    const maxAllowed = Math.round(bounds.listPrice * 1.15 * 100) / 100;
    const minAllowed = Math.round(bounds.listPrice * 0.85 * 100) / 100;
    if (price > bounds.listPrice && price > maxAllowed) {
      return `Price cannot be increased by more than 15% above the current price (${formatCurrency(maxAllowed)}).`;
    }
    if(price < bounds.listPrice && price < minAllowed){
      return `Price cannot be decreased by more than 15% below the current price (${formatCurrency(minAllowed)}).`;
    }
  }
  if (bounds.listPrice != null) {
    const amazonMaxLimit = Math.max(bounds.listPrice * 6, 200);
    if (price >= amazonMaxLimit) {
      return `Price exceeds Amazon's safety limit of ${formatCurrency(amazonMaxLimit)}.`;
    }
  }
  return null;
}

/**
 * Validates that a derived sale price stays within min/max boundaries.
 * Only call when a sale is active — the derived sale price is the proportionally
 * scaled price the customer sees, so it must respect the same bounds.
 */
export function validateDerivedSalePrice(
  derivedSalePrice: number,
  bounds: { min?: number; max?: number },
  formatCurrency: (v: number) => string
): string | null {
  if (bounds.min != null && derivedSalePrice < bounds.min) {
    return `The derived sale price (${formatCurrency(derivedSalePrice)}) would fall below the minimum price (${formatCurrency(bounds.min)}).`;
  }
  if (bounds.max != null && derivedSalePrice > bounds.max) {
    return `The derived sale price (${formatCurrency(derivedSalePrice)}) would exceed the maximum price (${formatCurrency(bounds.max)}).`;
  }
  return null;
}

/**
 * Validates custom min/max price bounds entered by the user in the UI.
 * Mirrors the ETL PricingPolicy.validateCustomBounds rules.
 * Returns separate per-field error strings so each input can show its own message.
 */
export function validateRepricingBounds(
  min: number | null,
  max: number | null,
  price: number | null,
  inboundPrice: number | null,
  formatCurrency: (v: number) => string,
  currentMax?: number | null,
): { minError: string | null; maxError: string | null } {
  let minError: string | null = null;
  let maxError: string | null = null;

  if (min != null && min < 1) {
    minError = 'Min price must be at least $1.00.';
  }
  if (max != null && max < 1) {
    maxError = 'Max price must be at least $1.00.';
  }
  if (min != null && max != null && min >= max) {
    if (!minError) minError = 'Min price must be less than max price.';
    if (!maxError) maxError = 'Max price must be greater than min price.';
  }
  if (!minError && min != null && price != null && min >= price) {
    minError = `Min price (${formatCurrency(min)}) must be less than your price (${formatCurrency(price)}).`;
  }
  if (max != null && !maxError) {
    if (price != null && max <= price) {
      maxError = `Max price (${formatCurrency(max)}) must be greater than your price (${formatCurrency(price)}).`;
    } else if (currentMax != null && currentMax > 0) {
      const maxAllowed = Math.round(currentMax * 1.30 * 100) / 100;
      const minAllowed = Math.round(currentMax * 0.70 * 100) / 100;
      if (max > maxAllowed) {
        maxError = `Max price cannot exceed 30% above the current max price (${formatCurrency(maxAllowed)}).`;
      }
      if (max < minAllowed) {
        maxError = `Max price cannot less than 30% below the current max price (${formatCurrency(minAllowed)}).`;
      }
    }
    if (!maxError && inboundPrice != null && inboundPrice > 0) {
      const amazonMaxAllowed = Math.max(inboundPrice * 6, 200);
      if (max >= amazonMaxAllowed) {
        maxError = `Max price must be less than ${formatCurrency(amazonMaxAllowed)} (Amazon limit: 6\u00d7 the current price or $200.00, whichever is higher).`;
      }
    }
  }

  return { minError, maxError };
}

// ---

function computeNewMax(
  activePrice: number,
  currentMax: number | undefined,
  suggestedMax: number | undefined,
): number | undefined {
  if (!currentMax) return undefined;
  if (!activePrice || !suggestedMax || activePrice <= currentMax) return currentMax;

  return suggestedMax ?? roundFloatNumber(currentMax + 2);
}

function computeNewMin(
  activePrice: number,
  currentMin: number | undefined,
  suggestedMin: number | undefined,
): number | undefined {
  if (!currentMin) return undefined;
  if (!activePrice || !suggestedMin || activePrice >= currentMin) return currentMin;

  return suggestedMin ?? roundFloatNumber(currentMin - 2);
}

export function computeNewMinMax(
  activePrice: number,
  suggestedMin: number | undefined,
  suggestedMax: number | undefined,
  currentMin: number | undefined,
  currentMax: number | undefined,
): { newMin: number | undefined; newMax: number | undefined } {
  return {
    newMin: computeNewMin(activePrice, currentMin, suggestedMin),
    newMax: computeNewMax(activePrice, currentMax, suggestedMax),
  };
}

// ---

const roundNumberOrUndefined = (num: number) => {
  return num ? roundFloatNumber(num) : undefined;
};

export const buildRepricingItem = (
  recommendation: Recommendation
): RepricingItem => {
  const {
    dataInput: { originalPrice },
  } = recommendation;

  const { factor } = recommendation.recommendationOutput.abstractState;

  const { suggestPrice, suggestedMin, suggestedMax, calculatedPrice } = recommendation.recommendationOutput;

  const isSaleActive = !!originalPrice.salePrice;

  return {
    id: recommendation.entityKey,
    valueState: recommendation.valueState,
    status: recommendation.status,
    state: recommendation?.recommendationOutput?.state,
    abstractState: recommendation?.recommendationOutput?.abstractState,
    factor,
    riskRatio: roundNumberOrUndefined(recommendation?.recommendationOutput?.riskRatio),
    // Current
    currentInboundPrice: roundNumberOrUndefined(originalPrice.inboundPrice),
    currentMin: roundNumberOrUndefined(originalPrice.minPrice),
    currentMax: roundNumberOrUndefined(originalPrice.maxPrice),

    // Calculated
    calculatedInboundPrice: roundNumberOrUndefined(calculatedPrice),

    // Proposed
    newInboundPrice: roundNumberOrUndefined(suggestPrice),
    newMin: roundNumberOrUndefined(suggestedMin ?? originalPrice.minPrice),
    newMax: roundNumberOrUndefined(suggestedMax ?? originalPrice.maxPrice),
    suggestedMin: roundNumberOrUndefined(suggestedMin),
    suggestedMax: roundNumberOrUndefined(suggestedMax),
    trendPercentage: roundFloatNumber((factor - 1) * 100),

    // Sale
    isSaleActive: isSaleActive,
    currentSalePrice: roundNumberOrUndefined(originalPrice.salePrice),
    saleSavePercent: isSaleActive
      ? Math.abs(roundFloatNumber(getPercentChange(originalPrice.inboundPrice, originalPrice.salePrice)))
      : undefined,
    context: recommendation.dataInput?.forecast,
    customPrice: roundNumberOrUndefined(recommendation?.recommendationOutput?.customPrice),
    customMinPrice: roundNumberOrUndefined(recommendation?.recommendationOutput?.customMinPrice),
    customMaxPrice: roundNumberOrUndefined(recommendation?.recommendationOutput?.customMaxPrice),
    isRevert: recommendation?.recommendationOutput?.isRevert === true,
    entitySnapshot: recommendation.entitySnapshot,
  };
};
